import { Component, ViewEncapsulation, Input, Output, EventEmitter, ViewChild, ElementRef, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ToastService, ToastType } from '@motes-sdk/providers';

import { MediaFile, MediaType, MediaFileType, AllowedMediaMap, GroupStatus, ImageFormat, urlToBase64WithoutExif, dataUrlToFile } from '../../models';

@Component({
    selector: 'upload-file',
    templateUrl: './upload-file.component.html',
    styleUrls: ['./upload-file.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class UploadFileComponent {
    // File to be uploaded and manipulate
    @Input() file: MediaFile;
    // Image size, ratio and format of file
    @Input() imageFormat: ImageFormat;

    @Input('allowedMediaTypes') set allowedTypes(allowedTypes: AllowedMediaMap) {
        this.setAllowedMediaTypes(allowedTypes);
    }

    @Input() hide: boolean = false;

    // State control variables
    @Input() isHovering: boolean = false;
    @Input() uploadStatus: GroupStatus = 'Idle';
    @Input() uploadProgress: number = 0;
    @Input('uploadError') set showUploadError(error: string){
        if(error) this.showError(error);
    }

    @Input() hideProgress: boolean = false;
    @Input() backgroundUrl: string = "";
    // Fix uploader aspect ratio to 16/9
    @Input() fixedAspectRatio: boolean = false;
    // Fix aspect ratio of empty uploader to 16/9
    @Input() fixedEmptyAspectRatio: boolean = true;
    // Show placeholder illustrating allowed media types
    @Input() showPlaceholder: boolean = true;
    @Input() showVideoCover: boolean = true;
    @Input() videoCover: string;

    @Output() fileSelected = new EventEmitter<MediaFile>();
    @Output() loadExternalMedia = new EventEmitter<void>();
    @Output() mediaTimeUpdate = new EventEmitter<number>();

    @ViewChild('fileInput', { static: true }) fileInput: ElementRef<HTMLInputElement>;
    @ViewChild('videoplayer') videoPlayer: ElementRef<HTMLVideoElement>;

    // Expose types to view
    public MediaType = MediaType;
    public MediaFileType = MediaFileType;

    // Sanitize url function for generic pipe with 'this' context 
    public sanitizeUrl = (url) => this.sanitizeVideoUrl(url);

    public isImageLoaded: boolean = false;

    public allowedMediaTypes: AllowedMediaMap = { IMAGE: true, VIDEO: true, SOUND: true, AMBISONIC: true };
    private readonly ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    private readonly ACCEPTED_VIDEO_TYPES = ['video/mp4','video/quicktime'];
    private readonly ACCEPTED_AUDIO_TYPES = ['audio/*'];
    private readonly MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
    public acceptedFileTypes: string = [...this.ACCEPTED_IMAGE_TYPES, ...this.ACCEPTED_VIDEO_TYPES, ...this.ACCEPTED_AUDIO_TYPES].join(',');
    public placeholderLoaded: { [type: string]: boolean } = {};

    constructor(private sanitizer: DomSanitizer, private toastService: ToastService) {
    }

    private setAllowedMediaTypes(allowedTypes: AllowedMediaMap) {
        this.allowedMediaTypes = allowedTypes;
        const acceptedMimeTypes = [];

        // Generate list of accepted file mime types from allowed media type input
        if(allowedTypes?.IMAGE) acceptedMimeTypes.push(...this.ACCEPTED_IMAGE_TYPES);
        if(allowedTypes?.VIDEO) acceptedMimeTypes.push(...this.ACCEPTED_VIDEO_TYPES);
        if(allowedTypes?.SOUND || allowedTypes?.AMBISONIC) acceptedMimeTypes.push(...this.ACCEPTED_AUDIO_TYPES);

        // Generate string of accepted mime types for HTML upload input
        this.acceptedFileTypes = acceptedMimeTypes.join(',');
    }

    public toggleHover(event: boolean) {
        this.isHovering = event;
    }

    public openFileSelector() {
        this.fileInput.nativeElement.click();
    }

    public async fileChangeEvent(ev: any) {
        // Get file list from drop zone or file input
        let fileList: FileList = ev instanceof FileList ? ev : ev.target.files;

        if(!fileList || !fileList.item(0) || fileList.item(0) == undefined) {
            return this.showError($localize`Sorry, the media you dragged cannot be dropped here. Please drag from somewhere else.`);
        }

        // Get first file in list
        let file: File = fileList.item(0);
        // Detect file type
        let mediaType = this.determineMediaType(file);
        let fileType = this.mediaToFileType(mediaType);

        // Is type allowed
        let isTypeAllowed = this.allowedMediaTypes[mediaType] || false;

        // Handle disallowed or unknown file types
        if(!isTypeAllowed) return this.showError($localize`Sorry, media file type '${file.type}' is not supported.`);

        // Block oversized images before EXIF strip / cropper (browser memory)
        if(mediaType === MediaType.IMAGE && file.size > this.MAX_IMAGE_SIZE_BYTES) {
            return this.showError($localize`Sorry, images must be under 10 MB. Please choose a smaller file.`);
        }

        // Read the input file from dom input
        let { url, strippedFile } = await this.readInputFile(file, mediaType);

        // Create event with file to be uploaded
        let uploadFile = new MediaFile(fileType, mediaType);
        uploadFile.url = url;
        uploadFile.blob = strippedFile;
        // Reset input file selector (in order to select same file again, which makes sense after editing or selecting a predefined profile image etc.)
        if(ev.srcElement?.value) ev.srcElement.value = "";
        // Notify parent
        this.fileSelected.emit(uploadFile);
    }

    /**
     * Temporary show an error to the user and then hide it again
     */
    private showError(error: string) : void {
        console.warn(error);
        this.toastService.addToast(error, ToastType.ERR);
    }

    /**
    * Determine the filetype of a dropped or selected file by meta data or filename (for ambisonic audio)
    */
    public determineMediaType(file: File): MediaType {
        if(!file || !file.type) return MediaType.UNDEFINED;

        // Check if file type ends with ambisonic attributes
        let isAmbisonic = file.name.endsWith(".amb.wav") || file.name.endsWith(".amb.mp3") || file.name.endsWith(".amb.mp4") || file.name.endsWith(".amb.m4a");

        if(file.type.includes('audio') && isAmbisonic) return MediaType.AMBISONIC;
        else if(file.type.includes('audio')) return MediaType.SOUND;
        else if(this.ACCEPTED_IMAGE_TYPES.some((type) => file.type.includes(type))) return MediaType.IMAGE;
        else if(this.ACCEPTED_VIDEO_TYPES.some((type) => file.type.includes(type))) return MediaType.VIDEO;
        else return MediaType.UNDEFINED;
    }

    public mediaToFileType(mediaType: MediaType): MediaFileType {
        switch(mediaType) {
            case MediaType.IMAGE: return MediaFileType.ORIGINAL;
            case MediaType.AMBISONIC: return MediaFileType.AMBISONIC;
            case MediaType.SOUND: return MediaFileType.SOUND;
            case MediaType.VIDEO: return MediaFileType.VIDEO;
            case MediaType.UNDEFINED: return MediaFileType.ORIGINAL;
        }
    }

    /**
     * Read the input file from HTML input and convert to data url
     */
    private async readInputFile(file: File, mediaType: MediaType): Promise<{url: string, strippedFile: File}> {
        let reader = new FileReader();
        reader.readAsDataURL(file);
        let dataUrl = await new Promise<string>((resolve) => {
            reader.onload = (ev: any) => {
                resolve(ev.target.result);
            }
        });
        // Bypass video and audio sanitization (for now we have to trust the super user allowed to upload these types)
        // However, it would be good to access how dangerous this potentially is 
        // - especially if we open up for normal users to upload other than their own profile images
        let safeUrl = this.sanitizer.bypassSecurityTrustUrl(dataUrl) as string
        let strippedFile = null;
        // Sanitize image files
        if(mediaType == MediaType.IMAGE) {
            // Lets apply and strip that damn EXIF data to avoid rotation issues
            const strippedUrl = await urlToBase64WithoutExif(dataUrl);
            // Convert base64 to File
            strippedFile = dataUrlToFile(strippedUrl, file.name);
            // Make a safe url for showing image in dom
            safeUrl = this.sanitizer.sanitize(SecurityContext.URL, strippedUrl);
        }
        return { url: safeUrl, strippedFile: strippedFile || file };
    }

    public sanitizeVideoUrl(url) {
        if(!url) {
            console.warn("Trying to sanitize empty video url");
            return "";
        }
        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    public externalMediaHandler() {
        this.loadExternalMedia.emit();
    }

    public handleMediaTimeChange(event: Event) {
        const target = event.target as HTMLVideoElement;
        if (!target) return;

        // Emit current media time in ms
        this.mediaTimeUpdate.emit(Math.floor(target.currentTime * 1000));
    }

    public playMedia() {
        this.showVideoCover = false;
        this.videoPlayer.nativeElement.play();
    }
}
